import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../lib/api';

export default function XeroCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [message, setMessage] = useState('Connecting to Xero...');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Authorization failed: ${error}`);
        setTimeout(() => navigate('/settings?tab=other'), 3000);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setMessage('Invalid callback parameters');
        setTimeout(() => navigate('/settings?tab=other'), 3000);
        return;
      }

      try {
        // Call backend to exchange code for tokens
        const response = await fetch(
          `${getBackendUrl()}/api/xero/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
          { credentials: 'include' }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to connect to Xero');
        }

        setStatus('success');
        setMessage(data.message || 'Successfully connected to Xero!');
        toast.success(data.message || 'Xero connected successfully!');
        
        setTimeout(() => navigate('/settings?tab=other'), 2000);
      } catch (err) {
        console.error('Xero callback error:', err);
        setStatus('error');
        setMessage(err.message || 'Failed to connect to Xero');
        toast.error(err.message || 'Failed to connect to Xero');
        setTimeout(() => navigate('/settings?tab=other'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              {status === 'processing' && (
                <>
                  <Loader2 className="h-12 w-12 animate-spin text-primary" />
                  <h2 className="section-title">Connecting to Xero</h2>
                  <p className="text-muted-foreground">{message}</p>
                </>
              )}
              
              {status === 'success' && (
                <>
                  <CheckCircle2 className="h-12 w-12 text-moss" />
                  <h2 className="section-title text-moss">Connected!</h2>
                  <p className="text-muted-foreground">{message}</p>
                  <p className="text-sm text-muted-foreground">Redirecting to settings...</p>
                </>
              )}
              
              {status === 'error' && (
                <>
                  <XCircle className="h-12 w-12 text-brick" />
                  <h2 className="section-title text-brick">Connection failed</h2>
                  <p className="text-muted-foreground">{message}</p>
                  <p className="text-sm text-muted-foreground">Redirecting to settings...</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
